import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  PersonOff as PersonOffIcon,
  Store as StoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import AddClinicDialog from './AddClinic';
import api from '../../api/api';
import Cards from '../../ReusableComponents/Cards/Cards';
import CustomTable from '../../ReusableComponents/Table/CustomTable';
import CustomSnackbar from '../../components/CustomSnackbar';
import SearchBar from '../../ReusableComponents/Search/SearchBar';
import { ButtonStyles } from '../../Styles/ButtonStyles';
import en_ja from '../../translations/en_ja';
import PersonIcon from '@mui/icons-material/Person';

const ClinicManagement = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [allClinics, setAllClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cardsData, setCardsData] = useState([])
  const [searchTerm, setSearchTerm] = useState('');
  const [editMode, setEditMode] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('success')

  const [selectedClinic, setSelectedClinic] = useState(null);
  const [originalClinicData, setOriginalClinicData] = useState(null);

  const handleEdit = (row) => {
    setSelectedClinic(row);
    setOriginalClinicData(row)
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDialogSubmit = () => {
    getAllClinics();
    getAllCards();
  }

  const handleDeactivate = (row) => {
    const obj = {
      status: !row.is_active
    }
    api.put(`v1/admin/clinic/${row.id}/status`, obj)
      .then((res) => {
        const status = res?.data?.clinic?.is_active ? en_ja["Active"] : en_ja["Inactive"];
        const message = `${en_ja["Clinic status updated to"]} ${status}`;
        setSnackbarMessage(message)
        setSnackbarType('success')
        setSnackbarOpen(true)
        getAllClinics();
        getAllCards();
      })
      .catch((err) => {
        console.error(err);
        setSnackbarMessage(en_ja["Could not update Status"])
        setSnackbarType('error')
        setSnackbarOpen(true)
      })
  };

  const columns = [
    { id: 'clinic_name', label: en_ja['Clinic Name'], sortable: true },
    { id: 'clinic_location', label: en_ja['Location'], sortable: true, maxWidth: 300, minWidth: 100 },
    { id: 'email', label: en_ja['Email'], sortable: true },
    { id: 'phone_no', label: en_ja['Contact'], sortable: true },
    {
      id: 'is_active', label: en_ja['Availability'], sortable: true,
      render: (row) => (
        <Typography color={row.is_active ? 'green' : 'red'}>
          {row.is_active ? en_ja['Available'] : en_ja['Not Available']}
        </Typography>
      ),
    },
    {
      id: 'actions',
      label: en_ja['Actions'],
      sortable: false,
      render: (row) => (
        <Box display="flex" justifyContent="start" gap={1}>
          <Tooltip title={en_ja["Edit Clinic"]} arrow>
            <IconButton
              aria-label="edit"
              size="small"
              onClick={() => handleEdit(row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={
              row.is_active
                ? en_ja["Deactivate Clinic"]
                : en_ja["Activate Clinic"]
            }
            arrow
          >
            <IconButton
              size="small"
              onClick={() => handleDeactivate(row)}
            >
              {row.is_active ? (
                <PersonIcon fontSize="small" sx={{ color: "#dc3545" }} />
              ) : (
                <PersonOffIcon fontSize="small" sx={{ color: "rgb(0 0 0 / 54%)" }} />
              )}
            </IconButton>
          </Tooltip>

        </Box>
      ),
    },
  ];

  useEffect(() => {
    getAllClinics();
    getAllCards();
  }, [])

  const getAllClinics = async () => {
    setLoading(true);
    try {
      const res = await api.get("/v1/admin/all-clinics");
      if (res) {
        setAllClinics(res.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDialog = () => {
    getAllClinics();
    setDialogOpen(false);
    setEditMode(false);
    setSelectedClinic(null);
  }

  const handleAddClinic = () => {
    setEditMode(false);
    setSelectedClinic(null);
    setDialogOpen(true);
  }

  const handleSnackbarClose = () => {
    setSnackbarOpen(false)
    setSnackbarMessage('')
    setSnackbarType('success')
  }

  const getAllCards = () => {
    api.get("/v1/admin/clinic-count")
      .then((res) => {
        if (res && res?.data?.data) {
          setCardsData(res?.data?.data);
        }
      })
      .catch((err) => console.error(err));
  };

  return (
    <Box sx={{ mt: 2 }}>

      <CustomSnackbar
        isOpen={snackbarOpen}
        type={snackbarType}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: 3,
        }}
      >
        <Cards
          title={en_ja["Total Clinics"]}
          value={cardsData?.totalClinics ?? 0}
          icon={<StoreIcon sx={{ color: "white", fontSize: 24 }} />}
        />

        <Cards
          title={en_ja["Active Clinics"]}
          value={cardsData?.activeClinics ?? 0}
          icon={<CheckCircleIcon sx={{ color: "white", fontSize: 24 }} />}
        />

        <Cards
          title={en_ja["Inactive Clinics"]}
          value={cardsData?.inactiveClinics ?? 0}
          icon={<CancelIcon sx={{ color: "white", fontSize: 24 }} />}
        />
      </Box>

      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        my: 3
      }}>
        <Box>
          <SearchBar
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Box>
        <Button sx={ButtonStyles} onClick={handleAddClinic}>
          {en_ja["Add Clinic"]}
        </Button>
      </Box>

      <Card
        sx={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          overflow: 'hidden'
        }}
      >
        <CustomTable
          columns={columns}
          rows={allClinics}
          loading={loading}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          showIndexColumn={true}
        />
      </Card>

      <AddClinicDialog
        open={dialogOpen}
        onClose={handleDialog}
        editMode={editMode}
        clinicData={selectedClinic}
        onSubmit={handleDialogSubmit}
        setSnackbarType={setSnackbarType}
        setSnackbarOpen={setSnackbarOpen}
        setSnackbarMessage={setSnackbarMessage}
        originalClinicData={originalClinicData}
      />
    </Box>
  );
};

export default ClinicManagement;
